import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api, { getErrorMessage } from '../../utils/api';

const ADMIN = '/admin';

// GET /admin/categories/get-all-categories (?is_active=)
// API returns { status, message, data: categories[] }
export const fetchCategories = createAsyncThunk(
  'category/fetchCategories',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-all-categories`, { params: filters });
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch categories');
    }
  }
);

// POST /admin/categories/create-category (FormData with optional image)
export const createCategory = createAsyncThunk(
  'category/createCategory',
  async (categoryData, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${ADMIN}/create-category`, categoryData);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to create category');
    }
  }
);

// POST /admin/categories/reorder-categories (body: { order: [{ id, display_order }] })
export const reorderCategories = createAsyncThunk(
  'category/reorderCategories',
  async (order, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${ADMIN}/reorder-categories`, { order });
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to reorder categories');
    }
  }
);

// GET /admin/get-categories-by-salon/:salonId (public)
export const fetchCategoriesBySalon = createAsyncThunk(
  'category/fetchCategoriesBySalon',
  async (salonId, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-categories-by-salon/${salonId}`);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch categories by salon');
    }
  }
);

// GET /admin/categories/get-category/:id
export const fetchCategoryById = createAsyncThunk(
  'category/fetchCategoryById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${ADMIN}/get-category/${id}`);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to fetch category');
    }
  }
);

// PUT /admin/categories/update-category/:id (FormData with optional image)
export const updateCategory = createAsyncThunk(
  'category/updateCategory',
  async ({ id, categoryData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`${ADMIN}/update-category/${id}`, categoryData);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to update category');
    }
  }
);

// DELETE /admin/categories/delete-category/:id
export const deleteCategory = createAsyncThunk(
  'category/deleteCategory',
  async (id, { rejectWithValue }) => {
    try {
      await api.delete(`${ADMIN}/delete-category/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to delete category');
    }
  }
);

// PATCH /admin/categories/toggle-active/:id
export const toggleCategoryActive = createAsyncThunk(
  'category/toggleCategoryActive',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.patch(`${ADMIN}/toggle-active/${id}`);
      return data?.data ?? data;
    } catch (error) {
      return rejectWithValue(getErrorMessage(error) || 'Failed to toggle category');
    }
  }
);

const initialState = {
  categories: [],
  categoriesBySalon: [],
  selectedCategory: null,
  loading: false,
  error: null,
};

const categorySlice = createSlice({
  name: 'category',
  initialState,
  reducers: {
    selectCategory: (state, action) => {
      state.selectedCategory = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = action.payload || [];
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.categories = [];
        state.error = action.payload || 'Failed to fetch categories';
      })
      .addCase(fetchCategoriesBySalon.fulfilled, (state, action) => {
        state.categoriesBySalon = action.payload || [];
      })
      .addCase(fetchCategoryById.fulfilled, (state, action) => {
        state.selectedCategory = action.payload;
      })
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) state.categories.push(action.payload);
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to create category';
      })
      .addCase(reorderCategories.fulfilled, (state, action) => {
        state.categories = action.payload || state.categories;
      })
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.categories.findIndex((c) => c.id === action.payload?.id);
        if (index !== -1 && action.payload) state.categories[index] = action.payload;
        if (state.selectedCategory?.id === action.payload?.id) {
          state.selectedCategory = action.payload;
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to update category';
      })
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.categories = state.categories.filter((c) => c.id !== action.payload);
        if (state.selectedCategory?.id === action.payload) state.selectedCategory = null;
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to delete category';
      })
      .addCase(toggleCategoryActive.fulfilled, (state, action) => {
        const index = state.categories.findIndex((c) => c.id === action.payload?.id);
        if (index !== -1 && action.payload) state.categories[index] = action.payload;
      });
  },
});

export const { selectCategory, clearError } = categorySlice.actions;
export default categorySlice.reducer;

export const selectCategories = (state) => state.category.categories;
export const selectCategoriesBySalon = (state) => state.category.categoriesBySalon;
export const selectSelectedCategory = (state) => state.category.selectedCategory;
export const selectCategoryLoading = (state) => state.category.loading;
export const selectCategoryError = (state) => state.category.error;

export function useCategory() {
  const dispatch = useDispatch();
  const categories = useSelector(selectCategories);
  const loading = useSelector(selectCategoryLoading);
  const error = useSelector(selectCategoryError);

  return {
    categories,
    loading,
    error,
    fetchCategories: useCallback((filters) => dispatch(fetchCategories(filters)), [dispatch]),
    createCategory: useCallback((data) => dispatch(createCategory(data)), [dispatch]),
    updateCategory: useCallback(({ id, categoryData }) => dispatch(updateCategory({ id, categoryData })), [dispatch]),
    deleteCategory: useCallback((id) => dispatch(deleteCategory(id)), [dispatch]),
    reorderCategories: useCallback((order) => dispatch(reorderCategories(order)), [dispatch]),
    fetchCategoriesBySalon: useCallback((salonId) => dispatch(fetchCategoriesBySalon(salonId)), [dispatch]),
    fetchCategoryById: useCallback((id) => dispatch(fetchCategoryById(id)), [dispatch]),
    toggleCategoryActive: useCallback((id) => dispatch(toggleCategoryActive(id)), [dispatch]),
  };
}
